'use client';

import { Box, Typography } from '@mui/material';
import styled from '@emotion/styled';
import { borderRadius, color, spacing, breakpoints } from '@/theme/tokens';
import ContactLocation from './ContactLocation';
import DepartmentContact from './DepartmentContact';

const SectionContainer = styled(Box)`
    background-color: ${color.white[0]};
    padding: ${spacing[16]};
    border-radius: ${borderRadius[8]};

    @media (min-width: ${breakpoints.sm}) {
        padding: ${spacing[24]};
    }

    @media (min-width: ${breakpoints.md}) {
        padding: ${spacing[32]};
    }
`;

const ContactsGrid = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'twoColsTablet',
}) <{ twoColsTablet?: boolean }>`
    display: grid;
    grid-template-columns: 1fr;

    /* Tablet: conditionally two columns */
    @media (min-width: ${breakpoints.sm}) and (max-width: ${breakpoints.md}) {
        ${(props) => props.twoColsTablet && `
            grid-template-columns: 1fr 1fr;
            column-gap: ${spacing[32]};
        `}
    }

    /* Desktop: always two columns */
    @media (min-width: ${breakpoints.md}) {
        grid-template-columns: 1fr 1fr;
        column-gap: ${spacing[32]};
    }
`;

// Wrapper for each contact card
const ContactCardWrapper = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'twoColsTablet',
}) <{ twoColsTablet?: boolean }>`
    padding-top: ${spacing[16]};
    padding-bottom: ${spacing[16]};
    position: relative;

    /* Mobile: simple border on all except last */
    border-bottom: 1px solid ${color.black[10]};

    &:last-child {
        border-bottom: none;
        padding-bottom: 0;
    }

    /* Tablet */
    @media (min-width: ${breakpoints.sm}) and (max-width: ${breakpoints.md}) {
        padding-top: ${spacing[24]};
        padding-bottom: ${spacing[24]};

        /* Two columns on tablet when enabled */
        ${(props) => props.twoColsTablet && `
            border-bottom: none;

            /* Left column items create full-width border */
            &:nth-child(odd)::after {
                content: '';
                position: absolute;
                bottom: 0;
                left: 0;
                width: calc(200% + ${spacing[32]});
                height: 1px;
                background-color: ${color.black[10]};
            }

            /* Last row: remove border */
            &:last-child:nth-child(odd)::after {
                display: none;
            }

            &:nth-last-child(2):nth-child(odd)::after {
                display: none;
            }

            &:nth-last-child(2):nth-child(odd),
            &:last-child {
                padding-bottom: 0;
            }
        `}
    }

    /* Desktop: always two columns */
    @media (min-width: ${breakpoints.md}) {
        padding-bottom: ${spacing[32]};
        padding-top: ${spacing[32]};
        border-bottom: none;

        /* Left column items create full-width border */
        &:nth-child(odd)::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 0;
            width: calc(200% + ${spacing[32]});
            height: 1px;
            background-color: ${color.black[10]};
        }

        /* Last row: remove border */
        &:last-child:nth-child(odd)::after {
            display: none;
        }

        &:nth-last-child(2):nth-child(odd)::after {
            display: none;
        }

        &:nth-last-child(2):nth-child(odd),
        &:last-child {
            padding-bottom: 0;
        }
    }
`;

type OfficeLocation = {
    id: string;
    name: string;
    address: string[] | string;
    mapUrl: string;
    manager?: string;
    phone?: string;
    fax?: string;
    email?: string;
    authOnly?: boolean;
};

export type BrokerLocation = {
    id: string;
    name: string;
    company?: string;
    address: string[] | string;
    mapUrl: string;
    manager?: string;
    phone?: string;
    email?: string;
    website?: string;
    authOnly?: boolean;
    ghanaOnly?: boolean;
};

type Department = {
    id: string;
    name: string;
    description: string;
    emails: string[];
};

type ContactSectionProps = {
    title: string;
    locations?: OfficeLocation[] | BrokerLocation[];
    departments?: Department[];
    isAuthenticated?: boolean;
    twoColumnsOnTabletForLocations?: boolean;
};

export default function ContactSection({
    title,
    locations,
    departments,
    isAuthenticated = false,
    twoColumnsOnTabletForLocations = false,
}: ContactSectionProps) {
    const filteredLocations = locations?.filter(
        (location) => !location.authOnly || isAuthenticated
    );
    const isOfficeContact = title === 'Office Contacts';

    return (
        <SectionContainer>
            <Typography variant="heading2bold" component="h2" color={color.gray[80]} sx={{ marginBottom: spacing[0] }}>
                {title}
            </Typography>

            {filteredLocations && (
                <ContactsGrid twoColsTablet={twoColumnsOnTabletForLocations}>
                    {filteredLocations.map((item) => (
                        <ContactCardWrapper
                            key={item.id}
                            twoColsTablet={twoColumnsOnTabletForLocations}
                        >
                            <ContactLocation
                                id={item.id}
                                name={item.name}
                                address={item.address}
                                mapUrl={item.mapUrl}
                                phone={item.phone}
                                fax={'fax' in item ? item.fax : undefined}
                                email={item.email}
                                website={'website' in item ? item.website : undefined}
                                manager={item.manager}
                                company={'company' in item ? item.company : undefined}
                                isOfficeContact={isOfficeContact}
                                isAuthenticated={isAuthenticated}
                            />
                        </ContactCardWrapper>
                    ))}
                </ContactsGrid>
            )}

            {departments && (
                <ContactsGrid>
                    {departments.map((item) => (
                        <ContactCardWrapper key={item.id}>
                            <DepartmentContact
                                name={item.name}
                                description={item.description}
                                emails={item.emails}
                            />
                        </ContactCardWrapper>
                    ))}
                </ContactsGrid>
            )}
        </SectionContainer>
    );
}
